import React from "react";

import { useAuth } from "@/hooks/useAuth";
import { Permission, useHasAnyPermission } from "@/hooks/usePermissions";

type ProtectProps = (
    | {
          permission: Permission;
          permissions?: undefined;
      }
    | {
          permission?: undefined;
          permissions: Permission[];
      }
) & {
    fallback?: React.ReactNode;
    children: React.ReactNode;
};

/**
 * Component that conditionally renders children based on user permissions
 * @param permission - Single permission to check
 * @param permissions - Array of permissions to check (requires at least one)
 * @param fallback - Content to render if permission check fails
 * @param children - Content to render if permission check passes
 */
export const Protect: React.FC<ProtectProps> = ({
    permission,
    permissions,
    fallback,
    children,
}) => {
    // Determine which permissions to check
    const permissionsToCheck = permissions ?? [permission];

    // Check permissions
    const hasPermission = useHasAnyPermission(permissionsToCheck);

    // If permission check passes, render children
    if (hasPermission) {
        return <>{children}</>;
    }

    // If fallback provided, render it. Otherwise render nothing.
    return fallback ? <>{fallback}</> : null;
};

/**
 * Hook for conditional rendering based on permissions
 * Use this when you need to conditionally show/hide UI elements
 */
/**
 * Component that shows an access denied message
 * Use this as a fallback for Protect when you want to show an error
 */
export const AccessDenied: React.FC<{ message?: string }> = ({ message }) => {
    const { signOut } = useAuth();
    return (
        <div className="mt-10 flex flex-col items-center space-y-2">
            <div>{message || "You don't have permission to access this feature"}</div>
            <button onClick={signOut} className="rounded bg-blue-200 px-4 py-2">
                Log out
            </button>
        </div>
    );
};

export const usePermissionCheck = (
    permission?: Permission | string,
    permissions?: (Permission | string)[]
): boolean => {
    const permissionsToCheck = permissions || (permission ? [permission] : []);
    return useHasAnyPermission(permissionsToCheck);
};
